import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

// Static base URL for backend-served mascot variants (generated via Nano Banana).
// The 4 "original" variants live on the emergent asset CDN; the 6 generated ones
// are served by our own backend from /api/static/mascots/*.png.
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export const MASCOT_VARIANTS = [
  {
    key: 'bold',
    label: 'Kaal & lachend',
    url: `${BACKEND}/api/static/mascots/bold_defringed.png`,
  },
  {
    key: 'hair',
    label: 'Met haar & lachend',
    url: `${BACKEND}/api/static/mascots/hair_defringed.png`,
  },
  {
    key: 'shocked',
    label: 'Geschrokken',
    url: `${BACKEND}/api/static/mascots/shocked_defringed.png`,
  },
  {
    key: 'tired',
    label: 'Moe',
    url: `${BACKEND}/api/static/mascots/tired_defringed.png`,
  },
  { key: 'laughing', label: 'Hard lachend',    url: `${BACKEND}/api/static/mascots/laughing.png` },
  { key: 'angry',    label: 'Boos',            url: `${BACKEND}/api/static/mascots/angry.png` },
  { key: 'thinking', label: 'Denkend',         url: `${BACKEND}/api/static/mascots/thinking.png` },
  { key: 'in_love',  label: 'Verliefd',        url: `${BACKEND}/api/static/mascots/in_love.png` },
  { key: 'gaming',   label: 'Gaming (headset)', url: `${BACKEND}/api/static/mascots/gaming.png` },
  { key: 'victory',  label: 'Overwinning',     url: `${BACKEND}/api/static/mascots/victory.png` },

  // Nieuwe varianten met échte transparantie (rembg)
  { key: 'hair_wink',     label: 'Haar · Knipoog',      url: `${BACKEND}/api/static/mascots/hair_wink.png` },
  { key: 'hair_cool',     label: 'Haar · Cool',         url: `${BACKEND}/api/static/mascots/hair_cool.png` },
  { key: 'hair_shocked',  label: 'Haar · Geschrokken',  url: `${BACKEND}/api/static/mascots/hair_shocked.png` },
  { key: 'hair_thinking', label: 'Haar · Denkend',      url: `${BACKEND}/api/static/mascots/hair_thinking.png` },
  { key: 'hair_love',     label: 'Haar · Verliefd',     url: `${BACKEND}/api/static/mascots/hair_love.png` },
  { key: 'hair_wave',     label: 'Haar · Zwaaien',      url: `${BACKEND}/api/static/mascots/hair_wave.png` },
  { key: 'bold_wink',     label: 'Kaal · Knipoog',      url: `${BACKEND}/api/static/mascots/bold_wink.png` },
  { key: 'bold_cool',     label: 'Kaal · Cool',         url: `${BACKEND}/api/static/mascots/bold_cool.png` },
  { key: 'bold_wave',     label: 'Kaal · Zwaaien',      url: `${BACKEND}/api/static/mascots/bold_wave.png` },
];

const DEFAULT_KEY = 'hair';
const STORAGE_KEY = 'jdv:mascot-variant';

function readCachedKey({ strict = false } = {}) {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && MASCOT_VARIANTS.some((x) => x.key === v)) return v;
  } catch {
    /* localStorage unavailable — fall through */
  }
  return strict ? null : DEFAULT_KEY;
}

function writeCachedKey(key) {
  try {
    window.localStorage.setItem(STORAGE_KEY, key);
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function mascotUrlFor(key) {
  const match = MASCOT_VARIANTS.find((v) => v.key === key);
  return (match || MASCOT_VARIANTS[0]).url;
}

/**
 * Public hook — reads the active mascot variant from overlay_settings and
 * returns the resolved image URL. Polls every 30s so admin changes propagate
 * without a page reload on the homepage / OBS mascot thanks overlay.
 * The initial value is read from localStorage so returning visitors don't
 * see a brief flash of the default variant before the API call resolves.
 */
export default function useMascot() {
  const cached = typeof window !== 'undefined' ? readCachedKey({ strict: true }) : null;
  const [variant, setVariant] = useState(cached ?? DEFAULT_KEY);
  const [ready, setReady] = useState(cached !== null);
  const initialVariantRef = useRef(variant);

  useEffect(() => {
    let alive = true;
    let lastKey = initialVariantRef.current;

    const load = async () => {
      try {
        const { data } = await api.get('/overlay-settings/public/mascot');
        const v = (data?.values?.variant || '').trim();
        const next = v && MASCOT_VARIANTS.some((x) => x.key === v) ? v : DEFAULT_KEY;
        if (!alive) return;
        if (next !== lastKey) {
          lastKey = next;
          setVariant(next);
        }
        writeCachedKey(next);
        setReady(true);
      } catch {
        // network hiccup — keep last known variant, do NOT reset to default
      }
    };
    load();
    const t = setInterval(load, 30_000);

    const onLocal = (e) => {
      const k = e?.detail?.variant;
      if (k && MASCOT_VARIANTS.some((x) => x.key === k) && alive) {
        lastKey = k;
        setVariant(k);
        writeCachedKey(k);
        setReady(true);
      }
    };
    window.addEventListener('mascot:changed', onLocal);

    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener('mascot:changed', onLocal);
    };
  }, []);

  return { variant, url: mascotUrlFor(variant), ready };
}
